import {combineLatest as observableCombineLatest, of as observableOf, Observable} from 'rxjs';

import {takeWhile, catchError, map} from 'rxjs/operators';
import {ChangeDetectionStrategy, ChangeDetectorRef, Component, NgZone} from '@angular/core';
import {AbstractControlComponent} from './abstract-control';
import {ValidatorFn, Validators} from '@angular/forms';
import {ViewTabComponent} from '../viewtab.component';
import {ErrorService} from '../../../error/error.service';

@Component({
  selector: 'ctrl-numericbox',
  template: `
    @if ((getCaughtObservable(isEnabled) | async) != true) {
      <p>{{ formControl.value }} {{ getCaughtObservable(postfix) | async }}</p>
    }
    @if (getCaughtObservable(isEnabled) | async) {
      <div>
        <mat-form-field floatLabel="never">
          <input
            matInput
            [placeholder]="(getCaughtObservable(_config?.Caption) | async)?.Values"
            [formControl]="formControl"
            type="number"
            [min]="getCaughtObservable(min) | async"
            [max]="getCaughtObservable(max) | async"
            step="1"
            autocomplete="off"
          />
        </mat-form-field>
        <span>{{ getCaughtObservable(postfix) | async }}</span>
        @if (formControl.touched) {
          <div>
            @if (formControl.hasError('pattern') || formControl.hasError('required')) {
              <mat-error>
                <language
                  key="ControlErrorRequired"
                  defaultValue="This field is required."
                ></language>
              </mat-error>
            }
            @if (formControl.hasError('min')) {
              <mat-error>
                <language
                  key="ControlErrorMin"
                  defaultValue="Please choose a value greater or equal to {0}."
                  [args]="[getCaughtObservable(min) | async]"
                ></language>
              </mat-error>
            }
            @if (formControl.hasError('max')) {
              <mat-error>
                <language
                  key="ControlErrorMax"
                  defaultValue="Please choose a value lower or equal to {0}."
                  [args]="[getCaughtObservable(max) | async]"
                ></language>
              </mat-error>
            }
          </div>
        }
      </div>
    }
  `,
  styleUrls: ['./ctrl-numericbox.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class CtrlNumericBoxComponent extends AbstractControlComponent {
  constructor(
    _parent: ViewTabComponent,
    changeDetector: ChangeDetectorRef,
    _ngZone: NgZone,
    errorService: ErrorService,
  ) {
    super(_parent, 'Value', 'Value', changeDetector, _ngZone, errorService);
  }

  min: Observable<number> = observableOf(0);
  max: Observable<number> = observableOf(255);

  protected initValidators(): void {
    this.min = this._config
      .getPropertyValues('MinValue')
      .pipe(map(val => CtrlNumericBoxComponent.parseMyInt(val, -2147483648)));
    this.max = this._config
      .getPropertyValues('MaxValue')
      .pipe(map(val => CtrlNumericBoxComponent.parseMyInt(val, 2147483648)));

    observableCombineLatest(
      this._config.getBooleanPropertyValues('Required'),
      this.min,
      this.max,
      (required: boolean, min: any, max: any) => {
        const validators: ValidatorFn[] = [];
        if (required === true) {
          validators.push(Validators.required);
          validators.push(Validators.pattern(/^\d+$/));
        }

        if (min) {
          validators.push(Validators.min(min));
        }

        if (max) {
          validators.push(Validators.max(max));
        }

        this.formControl.setValidators(validators);
        this.formControl.updateValueAndValidity({emitEvent: false});
      },
    )
      .pipe(
        catchError((error, caught) => this.handleError(error, caught)),
        takeWhile(() => this.alive),
      )
      .subscribe();
  }

  protected formatSource(value: any): any {
    if (typeof value === 'number') {
      return value;
    } else {
      value = CtrlNumericBoxComponent.parseMyInt(value, null);
      if (value == null) {
        return '?';
      } else {
        return value;
      }
    }
  }

  protected formatTarget(value: any): any {
    return value === '?' ? null : value;
  }

  get postfix(): Observable<string> {
    return this._config.getPropertyValues('Postfix').pipe(
      map(val => {
        if (val == null) {
          return '';
        }
        return val;
      }),
    );
  }

  private static parseMyInt(input: string, defaultValue: number): number {
    if (input == null) return defaultValue;
    let num = Number(input);
    if (num == null) {
      num = defaultValue;
    }
    return num;
  }
}
